import { useState } from "react"

import {
  AssetSalesCharge,
  AssetSalesChargeAttributes,
  AssetSalesChargeClass,
  AssetSalesChargeClasses,
  AssetSalesChargeRate,
} from "wealthmode-frontend/backend"
import {
  Box,
  Button,
  ButtonText,
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  HStack,
  Heading,
  Input,
  InputField,
  Text,
  VStack,
} from "wealthmode-frontend/gluestack/ui"
import { CheckIcon } from "wealthmode-frontend/icons"

import { PercentField } from "@/components/controls"
import { DraftRate, fromDrafts, newDraft, toDrafts } from "@/components/widgets/SalesChargeEditor/rates"
import { percentToRate, rateToPercent } from "@/lib/rate"

const CLASS_LABELS: Record<AssetSalesChargeClass, string> = {
  A: "A · on purchase",
  B: "B · on sale",
  C: "C · while held",
}

// What each class's breakpoints measure, as the label of the field entering
// one. A class without a label is charged at a single rate.
const BREAKPOINT_LABELS: Record<AssetSalesChargeClass, string | null> = {
  A: "Purchases from ($)",
  B: "Held for (years)",
  C: null,
}

/**
 * SalesChargeEditor curates the sales charge an asset carries: its class,
 * which decides when the charge is incurred and what its breakpoints measure,
 * the rates charged, and whether automated research may replace it. A class
 * charged by breakpoint takes a schedule whose lowest breakpoint is zero; the
 * others take a single rate.
 */
export function SalesChargeEditor({ salesCharge }: { salesCharge: AssetSalesCharge }) {
  const [shareClass, setShareClass] = useState(salesCharge.attributes.class)
  // The schedule and the single rate are kept apart, so switching class back
  // and forth keeps what was typed for each.
  const [drafts, setDrafts] = useState(() => scheduleDrafts(salesCharge.attributes))
  const [flatPercent, setFlatPercent] = useState(() => flatRatePercent(salesCharge.attributes))
  const [locked, setLocked] = useState(salesCharge.attributes.locked)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const breakpointLabel = shareClass === null ? null : BREAKPOINT_LABELS[shareClass]

  function update(key: number, change: Partial<Omit<DraftRate, "key">>) {
    setDrafts((current) => current.map((draft) => (draft.key === key ? { ...draft, ...change } : draft)))
  }

  async function save() {
    setSaving(true)
    setError(null)

    try {
      await salesCharge.saveCuration(shareClass, ratesToSave(shareClass, drafts, flatPercent), locked)

      setShareClass(salesCharge.attributes.class)
      setDrafts(scheduleDrafts(salesCharge.attributes))
      setFlatPercent(flatRatePercent(salesCharge.attributes))
      setLocked(salesCharge.attributes.locked)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <VStack className="gap-12">
      <VStack>
        <Heading size="md">Sales charge</Heading>
        <Text className="text-xs text-foreground-secondary">
          The class decides when the charge is incurred and what its breakpoints measure. Each rate applies from its
          breakpoint until the next breakpoint up takes over, and the lowest breakpoint must be 0. Class C is charged at
          a single rate.
        </Text>
      </VStack>

      <HStack className="gap-2">
        <ClassButton
          label="None"
          selected={shareClass === null}
          onPress={() => setShareClass(null)}
        />
        {AssetSalesChargeClasses.map((option) => (
          <ClassButton
            key={option}
            label={CLASS_LABELS[option]}
            selected={shareClass === option}
            onPress={() => setShareClass(option)}
          />
        ))}
      </HStack>

      {shareClass === null ? null : breakpointLabel === null ? (
        <PercentField
          label="Rate"
          value={flatPercent}
          onChange={setFlatPercent}
        />
      ) : (
        <VStack className="gap-6">
          {drafts.map((draft) => (
            <HStack
              key={draft.key}
              className="items-end gap-4"
            >
              <BreakpointField
                label={breakpointLabel}
                value={draft.breakpoint}
                onChange={(breakpoint) => update(draft.key, { breakpoint })}
              />
              <PercentField
                label="Rate"
                value={draft.percent}
                onChange={(percent) => update(draft.key, { percent })}
              />
              <Button
                size="sm"
                variant="outline"
                onPress={() => setDrafts((current) => current.filter((other) => other.key !== draft.key))}
              >
                <ButtonText>Remove</ButtonText>
              </Button>
            </HStack>
          ))}

          <HStack>
            <Button
              size="sm"
              variant="outline"
              // A schedule starts at zero, so the first rate added does too.
              onPress={() => setDrafts((current) => [...current, newDraft(current.length === 0 ? "0" : "")])}
            >
              <ButtonText>Add rate</ButtonText>
            </Button>
          </HStack>
        </VStack>
      )}

      <VStack className="gap-1">
        <Checkbox
          value="locked"
          isChecked={locked}
          onChange={setLocked}
          size="md"
        >
          <CheckboxIndicator>
            <CheckboxIcon as={CheckIcon} />
          </CheckboxIndicator>
          <CheckboxLabel>Locked</CheckboxLabel>
        </Checkbox>
        <Text className="text-xs text-foreground-secondary">
          Automated research never replaces a locked sales charge. Lock None to record that the asset carries none.
        </Text>
      </VStack>

      {error ? (
        <Box className="rounded-md border border-border-medium bg-background-tertiary px-3 py-2">
          <Text className="text-sm text-foreground-secondary whitespace-pre-wrap">{error}</Text>
        </Box>
      ) : null}

      <HStack>
        <Button
          action="primary"
          className="bg-background-contrast"
          isDisabled={saving}
          onPress={() => void save()}
        >
          <ButtonText className="text-foreground-contrast">{saving ? "Saving…" : "Save sales charge"}</ButtonText>
        </Button>
      </HStack>
    </VStack>
  )
}

function ClassButton({ label, selected, onPress }: { label: string; selected: boolean; onPress: () => void }) {
  return (
    <Button
      size="sm"
      variant={selected ? "solid" : "outline"}
      className={selected ? "bg-background-contrast" : undefined}
      onPress={onPress}
    >
      <ButtonText className={selected ? "text-foreground-contrast" : undefined}>{label}</ButtonText>
    </Button>
  )
}

function BreakpointField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <FormControl>
      <FormControlLabel>
        <FormControlLabelText>{label}</FormControlLabelText>
      </FormControlLabel>
      <Input
        variant="outline"
        className="w-48"
      >
        <InputField
          value={value}
          onChangeText={onChange}
          keyboardType="decimal-pad"
          autoCorrect={false}
        />
      </Input>
    </FormControl>
  )
}

function ratesToSave(
  shareClass: AssetSalesChargeClass | null,
  drafts: DraftRate[],
  flatPercent: string,
): AssetSalesChargeRate[] {
  if (shareClass === null) {
    return []
  }

  if (BREAKPOINT_LABELS[shareClass] === null) {
    const rate = percentToRate(flatPercent)
    if (rate === null) {
      throw new Error("Enter a rate, or choose no sales charge")
    }

    return [{ breakpoint: null, rate }]
  }

  if (drafts.length === 0) {
    throw new Error("Add at least one rate, or choose no sales charge")
  }

  return fromDrafts(drafts)
}

function scheduleDrafts(attributes: AssetSalesChargeAttributes): DraftRate[] {
  return toDrafts(attributes.rates.filter((rate) => rate.breakpoint !== null))
}

function flatRatePercent(attributes: AssetSalesChargeAttributes): string {
  const flat = attributes.rates.find((rate) => rate.breakpoint === null)

  return rateToPercent(flat?.rate ?? null)
}
